import {
  AttributeSelectionsByAttributeId,
  AttributeValueType,
  EngineResult,
  ProductAttribute,
  ProductAttributeMeta,
  ProductAttributeValue,
  ProductConfigSection,
  ProductPreset,
  SizeValidationRule,
  SizeValidationOperator,
  EngineSizeValidation,
  SizeValue,
  SizeAttributeDimension,
} from "@sourceful/shared-types";
import { getSelectedAttributeValues } from "@components/configuratorV2/configuration-helpers";
import { ChangeTagProps } from "../HorizontalRadioCard/HorizontalRadioCard";
import startCase from "lodash/startCase";
import { OneStepChangeMetadata } from "@sourceful/shared-utils/engine-utils";

interface GetAttributeDetailsProps {
  activeSectionAttributes: ProductAttributeMeta[] | undefined;
  currentConfigItemAttributeSelection: AttributeSelectionsByAttributeId;
}

export interface AttributeDetails {
  attributeMetadata: ProductAttribute;
  selectedValueLabel: string;
  isSelectedValueNamedRFQ: Boolean;
  selectedValueId?: string;
}

export const getAttributeDetails = ({
  activeSectionAttributes,
  currentConfigItemAttributeSelection,
}: GetAttributeDetailsProps): AttributeDetails[] => {
  return (
    activeSectionAttributes?.map(attribute => {
      const attributeMetadata =
        currentConfigItemAttributeSelection[attribute.attributeId].attribute;

      const selectedValues = getSelectedAttributeValues(
        currentConfigItemAttributeSelection,
        attribute.attributeId
      );

      // In practice, we only have one selected value (tech debt that should be cleaned up)
      const selectedAttrValue = selectedValues[0];

      const selectedValueMetadata = attributeMetadata.options.find(
        option => option.value.id === selectedAttrValue.id
      );

      return {
        attributeMetadata: attributeMetadata,
        selectedValueId: selectedValueMetadata?.id,
        selectedValueLabel: selectedValueMetadata?.label || "",
        isSelectedValueNamedRFQ: Boolean(selectedValueMetadata?.value.rfq),
      };
    }) || []
  );
};

interface GetActiveAttributeDetails {
  currentConfigItemAttributeSelection: AttributeSelectionsByAttributeId;
  activeAttribute: ProductAttribute | null;
}

export const getActiveAttributeDetails = ({
  currentConfigItemAttributeSelection,
  activeAttribute,
}: GetActiveAttributeDetails) => {
  if (activeAttribute) {
    const attributeMetadata = currentConfigItemAttributeSelection[activeAttribute.id].attribute;

    const selectedValues = getSelectedAttributeValues(
      currentConfigItemAttributeSelection,
      activeAttribute.id
    );

    // In practice, we only have one selected value (tech debt that should be cleaned up)
    const selectedAttrValue = selectedValues[0];

    const selectedValueMetadata = attributeMetadata.options.find(
      option => option.value.id === selectedAttrValue.id
    );

    return {
      id: attributeMetadata.id,
      title: attributeMetadata.title,
      label: selectedValueMetadata?.label || "",
    };
  }
};

export const getSelectionDialogTitle = (
  activeSection: ProductConfigSection,
  activeAttribute: ProductAttribute | null,
  override?: string
) => {
  if (override) {
    return override;
  }

  if (!activeAttribute) {
    return activeSection.title;
  }

  const attributeDisplayMeta = activeSection.attributes?.find(
    meta => meta.attributeId === activeAttribute.id
  );

  if (attributeDisplayMeta?.isTitleHidden || activeSection.title === activeAttribute.title) {
    return activeSection.title;
  }

  if (activeSection.title.toUpperCase() === activeAttribute.title.toUpperCase()) {
    return activeSection.title;
  }

  return `${activeSection.title} - ${activeAttribute.title}`;
};

export const getOptionEngineData = (
  option: ProductAttributeValue,
  attributeEngineData: { [attributeValueOrId: string]: EngineResult }
) => {
  if (typeof option.value === "string" || typeof option.value === "number") {
    return attributeEngineData[option.value];
  }

  return attributeEngineData[option.value.id];
};

interface FilterAttributOptionsArgs {
  activeAttributeOptions: ProductAttributeValue[];
  attributeEngineData: { [attributeValueOrId: string]: EngineResult };
  isCustomQuote: boolean;
}

export const filterAttributeOptionsByEngineResults = ({
  activeAttributeOptions,
  attributeEngineData,
  isCustomQuote,
}: FilterAttributOptionsArgs): ProductAttributeValue[] => {
  return isCustomQuote
    ? activeAttributeOptions
    : activeAttributeOptions.filter(option => {
        return getOptionEngineData(option, attributeEngineData) !== undefined;
      });
};

interface DisableResetButtonProps {
  currentConfigItemPreset: ProductPreset | null;
  activeAttribute: ProductAttribute | null;
  currentConfigItemProductAttributes: { ids: string[]; entities: Record<string, ProductAttribute> };
  selectedAttributeValueId: string;
  isEngineLoading: boolean;
}

export const disableResetButton = ({
  activeAttribute,
  currentConfigItemPreset,
  currentConfigItemProductAttributes,
  selectedAttributeValueId,
  isEngineLoading,
}: DisableResetButtonProps) => {
  if (isEngineLoading) {
    return true;
  }

  if (!currentConfigItemPreset || !activeAttribute) {
    return true;
  }

  const presetAttributeValues = getSelectedAttributeValues(
    currentConfigItemPreset.attributes,
    activeAttribute.id
  );
  const presetAttributeValue = presetAttributeValues[0];

  const attribute = currentConfigItemProductAttributes.entities[activeAttribute.id];
  const selectedValue = attribute.options.find(
    option => option.value.id === selectedAttributeValueId
  );

  if (presetAttributeValue.id === selectedValue?.value.id) {
    return true;
  } else {
    return false;
  }
};

export const checkCustomOptionSelected = (
  isCustomOptionEnabled: boolean,
  selectedOptions: AttributeValueType[],
  customOptions: ProductAttributeValue[]
): boolean => {
  return (
    selectedOptions.length > 0 &&
    isCustomOptionEnabled &&
    // there will only ever be max 1 item in the selectedOptions for custom options as they must be of type radio
    !!customOptions?.find(i => i.value.id === selectedOptions[0].id)
  );
};

// TODO: export radiocardprops and pick<>
export type EngineMetadataProps = {
  priceDetails?: string;
  priceTag?: ChangeTagProps;
  carbonDetails?: string;
  carbonTag?: ChangeTagProps;
};

export const getEngineMetadataProps = (
  engineMetadata: OneStepChangeMetadata | undefined
): EngineMetadataProps => {
  const props: EngineMetadataProps = {};

  if (!engineMetadata) return props;

  props.priceDetails = `${engineMetadata.price.value} per unit`;

  props.priceTag = engineMetadata.price.change
    ? {
        tagChange: engineMetadata.price.isIncreasing ? "increase" : "decrease",
        text: engineMetadata.price.change || "",
      }
    : undefined;

  props.carbonDetails = engineMetadata.lca.value;

  props.carbonTag = engineMetadata.lca.change
    ? {
        tagChange: engineMetadata.lca.isIncreasing ? "increase" : "decrease",
        text: engineMetadata.lca.change || "",
      }
    : undefined;

  return props;
};

export const validateSizeRule = (rule: SizeValidationRule, value: SizeValue) => {
  const val1 = value[rule.value];
  const val2 = value[rule.value2];

  if (!val1 || !val2) return false;

  switch (rule.operator) {
    case SizeValidationOperator.EQUALS:
      return val1 === val2;

    case SizeValidationOperator.GREATER_THAN:
      return val1 > val2;

    case SizeValidationOperator.LESS_THAN:
      return val1 < val2;
    case SizeValidationOperator.NOT_EQUALS:
      return val1 !== val2;
    default:
      return true;
  }
};

export const getSizeRuleMessage = (rule: SizeValidationRule) => {
  const map = {
    [SizeValidationOperator.EQUALS]: "be equal",
    [SizeValidationOperator.GREATER_THAN]: "be greater than",
    [SizeValidationOperator.LESS_THAN]: "be less than",
    [SizeValidationOperator.NOT_EQUALS]: "not be equal",
  };

  const operator = map[rule.operator];

  return `${startCase(rule.value)} should ${operator} to ${rule.value2}`;
};

export const validateSize = (
  validation: EngineSizeValidation,
  value: SizeValue
): { isValid: boolean; error: string } => {
  for (let dimension of validation.required_dimensions) {
    if (!value[dimension]) {
      return { isValid: false, error: "Please fill in all inputs" };
    }

    if (
      validation &&
      ((value[dimension] as number) > validation[dimension].max ||
        (value[dimension] as number) < validation[dimension].min)
    ) {
      return { isValid: false, error: "Please make sure all dimensions are valid" };
    }
  }

  if (validation.rules) {
    for (let rule of validation.rules) {
      const isValid = validateSizeRule(rule, value);
      if (!isValid) {
        const message = getSizeRuleMessage(rule);
        return { isValid, error: message };
      }
    }
  }

  return { isValid: true, error: "" };
};

interface FindMatchingSizeProps {
  productAttributeValues: ProductAttributeValue[];
  requiredDimensions: SizeAttributeDimension[] | undefined;
  size: SizeValue;
}

export const findMatchingSize = ({
  productAttributeValues,
  requiredDimensions,
  size,
}: FindMatchingSizeProps) => {
  return productAttributeValues.find(option => {
    const value = option.value as SizeValue;
    return requiredDimensions?.every(dimension => {
      return value[dimension] === size[dimension];
    });
  });
};

export enum SizeExistsIn {
  "STANDARD_SIZES" = "standardSizes",
  "CUSTOM_SIZES" = "customSizes",
}
interface SizeAlreadyExistsProps {
  size: SizeValue;
  sizeAttribute: ProductAttribute;
  customValues: ProductAttributeValue[];
}

interface SizeAlreadyExists {
  sizeExistsIn: SizeExistsIn | null;
  sizeExists: boolean;
}

export const sizeAlreadyExists = ({
  size,
  sizeAttribute,
  customValues,
}: SizeAlreadyExistsProps): SizeAlreadyExists => {
  const matchingSizeInStandardSizes = findMatchingSize({
    productAttributeValues: sizeAttribute.options,
    requiredDimensions: sizeAttribute.requiredDimensions,
    size: size,
  });

  const matchingSizeInCustomSizes = findMatchingSize({
    productAttributeValues: customValues,
    requiredDimensions: sizeAttribute.requiredDimensions,
    size: size,
  });

  const sizeExists = Boolean(matchingSizeInStandardSizes || matchingSizeInCustomSizes);

  const sizeExistsIn = sizeExists
    ? matchingSizeInCustomSizes
      ? SizeExistsIn.CUSTOM_SIZES
      : SizeExistsIn.STANDARD_SIZES
    : null;

  return {
    sizeExists: sizeExists,
    sizeExistsIn: sizeExistsIn,
  };
};
